import { useEffect, useState } from 'react';

import logger from 'saddlebag-logger';

import {
  ASHBY_JOBS_URL,
  GREENHOUSE_JOBS_URL,
  GREENHOUSE_SINGLE_JOB_BASE_URL,
} from '../utils/constants';

type GreenhouseJobDepartment = {
  id: number;
  name: string;
  parent_id: number;
  child_ids: number[];
};

type GreenhouseJob = {
  id: string;
  absolute_url: string;
  internal_job_id?: number;
  requisition_id: number;
  location: { name: string };
  departments: GreenhouseJobDepartment[];
  offices: [
    {
      id: number;
      name: string;
      location: string;
      parent_id: number;
      child_ids: number[];
    },
  ];
  title: string;
  metadata: [{ name: string; value: string }];
  content: string;
  status: number;
};

type AshbySecondaryLocation = {
  location: string;
  address: {
    addressLocality: string;
    addressRegion: string;
    addressCountry: string;
  } | null;
} | null;

export type AshbyJob = {
  id: string;
  title: string;
  department: string;
  team: string;
  employmentType: string;
  location: string;
  secondaryLocations: AshbySecondaryLocation[];
  publishedAt: string;
  isListed: boolean;
  isRemote: boolean | null;
  workplaceType: string | null;
  address: {
    postalAddress: {
      addressLocality: string;
      addressRegion: string;
      addressCountry: string;
    };
  } | null;
  jobUrl: string;
  applyUrl: string;
  descriptionHtml: string;
  descriptionPlain: string;
};

// Ashby splits a job's locations into a primary `location` string plus a
// `secondaryLocations` array, unlike Greenhouse which put everything in a single
// free-text field (e.g. "Edinburgh or London"). These helpers reconstruct the
// full location set for display and filtering.
export const getAshbyJobLocations = (job: AshbyJob): string[] => {
  const secondary = (job.secondaryLocations ?? [])
    .map((secondaryLocation) => secondaryLocation?.location)
    .filter((location): location is string => Boolean(location));
  return [job.location, ...secondary].filter(Boolean);
};

// Format the location set as "A, B or C" for display.
export const formatAshbyJobLocations = (job: AshbyJob): string => {
  const locations = getAshbyJobLocations(job);
  if (locations.length <= 1) {
    return locations[0] ?? '';
  }
  return `${locations.slice(0, -1).join(', ')} or ${locations[locations.length - 1]}`;
};

export const useGetAshbyJobs = () => {
  const [jobs, setJobs] = useState<AshbyJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(ASHBY_JOBS_URL);
        const resData = (await res.json()) as { jobs: AshbyJob[] };
        if (resData) {
          const validJobs = resData.jobs
            .filter((ashbyJob: AshbyJob) => ashbyJob.isListed)
            // Sort by publishedAt descending (newest first), replacing
            // Greenhouse's requisition_id sort.
            .sort(
              (a: AshbyJob, b: AshbyJob) =>
                Date.parse(b.publishedAt) - Date.parse(a.publishedAt),
            );
          setJobs(validJobs);
        }
      } catch (err) {
        // This is the existing behaviour, not ideal handling but is unchanged
        logger.logError(err instanceof Error ? err : new Error(String(err)), {
          message: 'useGetAshbyJobs: fetch failed',
          component: 'JobsApp',
        });
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  return { jobs, loading };
};

export const useGetSingleAshbyJob = (jobId: string) => {
  const [job, setJob] = useState<AshbyJob | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Reset on jobId change so SPA navigation between job pages doesn't briefly
    // show the previous job (or keep it when the new id has no match).
    setLoading(true);
    setJob(null);

    const fetchJob = async () => {
      try {
        // It doesn't look like there's a way to request a single job from the public Ashby API so we have to do the
        // filtering on our end
        const res = await fetch(ASHBY_JOBS_URL);
        const resData = (await res.json()) as { jobs: AshbyJob[] };
        if (resData) {
          const validJobs = resData.jobs.filter(
            (ashbyJob: AshbyJob) => ashbyJob.isListed && ashbyJob.id === jobId,
          );
          // Explicitly clear to null on no match so JobPage renders the
          // "no longer available" message instead of stale data.
          setJob(validJobs.length === 1 ? validJobs[0] : null);
        }
      } catch (err) {
        // This is the existing behaviour, not ideal handling but is unchanged
        logger.logError(err instanceof Error ? err : new Error(String(err)), {
          message: 'useGetSingleAshbyJob: fetch failed',
          component: 'JobsApp',
        });
        setJob(null);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId]);

  return { job, loading };
};

export const useGetJobs = () => {
  const [jobs, setJobs] = useState<GreenhouseJob[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(GREENHOUSE_JOBS_URL);
        const resData = (await res.json()) as { jobs: GreenhouseJob[] };
        if (resData) {
          const validJobs = resData.jobs
            .filter((job: GreenhouseJob) => {
              const hide = job.metadata.find(
                (meta) => meta.name === 'Hide from job site',
              );
              if (!hide) {
                return true;
              }
              return !hide.value;
            })
            .sort(
              (a: GreenhouseJob, b: GreenhouseJob) =>
                b.requisition_id - a.requisition_id,
            );
          setJobs(validJobs);
        }
      } catch (err) {
        // This is the existing behaviour, not ideal handling but is unchanged
        logger.logError(err instanceof Error ? err : new Error(String(err)), {
          message: 'useGetJobs: fetch failed',
          component: 'JobsApp',
        });
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  return { jobs, loading };
};

export const useGetSingleJob = (jobId: string) => {
  const [job, setJob] = useState<GreenhouseJob>();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(`${GREENHOUSE_SINGLE_JOB_BASE_URL}/${jobId}`);
        const listing = (await res.json()) as GreenhouseJob;
        if (listing) {
          setJob(listing);
        }
      } catch (err) {
        // This is the existing behaviour, not ideal handling but is unchanged
        logger.logError(err instanceof Error ? err : new Error(String(err)), {
          message: 'useGetSingleJob: fetch failed',
          component: 'JobsApp',
          jobId,
        });
        setJob(undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [jobId]);

  return { job, loading };
};
